import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Pencil } from "lucide-react";
import { adminStats, adminSetCap } from "@/lib/api";
import { Card, StatCard, SectionTitle, Spinner, KawaiiModal } from "./kit";
import UnlockPaceCard from "./UnlockPaceCard";
import WrapupCard from "./WrapupCard";

export default function OverviewSection() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editOpen, setEditOpen] = useState(false);
    const [capInput, setCapInput] = useState("");
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try {
            const s = await adminStats();
            setStats(s);
            setCapInput(String(s.beta_user_cap));
        } catch {
            /* handled by parent auth gate */
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const saveCap = async () => {
        const n = parseInt(capInput, 10);
        if (Number.isNaN(n) || n < 0) {
            toast.error("that cap needs to be a whole number 🍡");
            return;
        }
        setSaving(true);
        try {
            await adminSetCap(n);
            toast.success(`guest list updated — ${n} spots 🍡`);
            setEditOpen(false);
            await load();
        } catch {
            toast.error("couldn't save that, try again in a sec");
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <Spinner label="counting Mochi's guests…" />;
    if (!stats) return null;

    const pct = stats.beta_user_cap > 0
        ? Math.min(100, Math.round((stats.beta_user_count / stats.beta_user_cap) * 100))
        : 0;

    return (
        <div className="space-y-6" data-testid="admin-overview">
            {/* Hero cap card */}
            <Card className="p-6" data-testid="overview-cap-card">
                <div className="flex items-start justify-between">
                    <div>
                        <span className="text-warm-500 text-xs uppercase tracking-wide font-semibold">
                            Beta guests
                        </span>
                        <div className="mt-2 flex items-baseline gap-2">
                            <span className="font-display text-6xl text-terracotta" data-testid="overview-beta-count">
                                {stats.beta_user_count}
                            </span>
                            <span className="font-display text-2xl text-warm-500">
                                / {stats.beta_user_cap}
                            </span>
                        </div>
                        <p className="text-warm-500 text-sm mt-1">
                            {stats.spots_remaining} cozy spots left 🍵
                        </p>
                    </div>
                    <button
                        onClick={() => setEditOpen(true)}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-semibold text-warm-800 bg-surface hover:bg-surface-hover transition-colors"
                        data-testid="overview-edit-cap-btn"
                    >
                        <Pencil className="w-3.5 h-3.5" /> Edit cap
                    </button>
                </div>
                <div className="mt-5 h-3 rounded-full bg-surface overflow-hidden">
                    <div
                        className="h-full bg-terracotta rounded-full transition-all"
                        style={{ width: `${pct}%` }}
                        data-testid="overview-cap-progress"
                    />
                </div>
            </Card>

            {/* Secondary stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <StatCard
                    label="Active codes"
                    value={stats.active_codes}
                    accent="sage"
                    sub={`${stats.total_codes} total ever`}
                    testid="overview-active-codes"
                />
                <StatCard
                    label="Allowlist"
                    value={stats.allowlist_entries}
                    accent="mustard"
                    sub="emails saved"
                    testid="overview-allowlist-count"
                />
                <StatCard
                    label="New this week"
                    value={stats.signups_last_7_days}
                    accent="terracotta"
                    sub="beta signups · 7 days"
                    testid="overview-signups-7d"
                />
            </div>

            <UnlockPaceCard />

            <WrapupCard />

            <KawaiiModal
                open={editOpen}
                onClose={() => setEditOpen(false)}
                title="Set the guest list size"
                testid="edit-cap-modal"
            >
                <p className="text-warm-800 text-sm mb-4">
                    How many friends can join the beta at once?
                </p>
                <input
                    type="number"
                    min="0"
                    value={capInput}
                    onChange={(e) => setCapInput(e.target.value)}
                    className="w-full px-4 py-3 rounded-2xl bg-surface border border-warm-300/40 text-warm-900 focus:outline-none focus:ring-2 focus:ring-terracotta/40 mb-6"
                    data-testid="edit-cap-input"
                />
                <div className="flex gap-3 justify-end">
                    <button
                        onClick={() => setEditOpen(false)}
                        className="px-4 py-2 rounded-full text-sm font-semibold text-warm-800 bg-surface hover:bg-surface-hover"
                        data-testid="edit-cap-cancel"
                    >
                        Never mind
                    </button>
                    <button
                        onClick={saveCap}
                        disabled={saving}
                        className="px-4 py-2 rounded-full text-sm font-semibold text-cream bg-terracotta hover:bg-terracotta-hover disabled:opacity-60"
                        data-testid="edit-cap-save"
                    >
                        {saving ? "saving…" : "Save cap"}
                    </button>
                </div>
            </KawaiiModal>
        </div>
    );
}
